import { getGetInventoryQueryOptions } from "@/requests/gen/react-query/inventory";
import { useGetInventoryLots } from "@/requests/gen/react-query/inventory-lots";
import { Header } from "@/components/universal/header";
import { Button } from "@/components/ui/button";
import {
  createFileRoute,
  Link,
  linkOptions,
  type LinkProps,
} from "@tanstack/react-router";
import { Pencil } from "lucide-react";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { IncomingOrdersDialog } from "./-incoming-orders-dialog";
import { AddInventoryLotModal } from "./-add-inventory-batch-modal";
import { MassAdjustmentModal } from "../-mass-adjustment-modal";
import { Badge } from "@/components/ui/badge";
import formatUSD from "@/lib/format-currency";
import {
  ParamDataTable,
  ParamDataTableToolbar,
  ParamSearchFilter,
  ParamClearButton,
  dataTableSearchParsers,
} from "@/components/ui/param-data-table";
import { useQueryStates } from "nuqs";
import { ColumnDef } from "@tanstack/react-table";
import { keepPreviousData, useSuspenseQuery } from "@tanstack/react-query";
import { suspenseOptions } from "@/lib/suspense-query";
import { ClipboardList } from "lucide-react";
import { AssignLotModal } from "@/components/modals/assign-lot-modal";
import { EditLotActionDialog } from "@/components/modals/edit-lot-action-dialog";
import { safeFormat } from "@/lib/safe-date-format";

export const Route = createFileRoute(
  "/_private/_inventory-360/inventory/$inventoryId/",
)({
  loader: ({ context: { queryClient }, params: { inventoryId } }) =>
    queryClient.ensureQueryData(getGetInventoryQueryOptions(inventoryId)),
  component: InventoryDetails,
});

interface LotTableProps {
  link?: LinkProps;
  lot_number: string | null;
  order_quantity: number;
  quantity_on_hand: number;
  avg_unit_cost: number;
  total_cost: number;
  earliest_received_at: string | null;
  earliest_expiration_date: string | null;
  action: React.ReactNode;
}

const lotColumns: ColumnDef<LotTableProps>[] = [
  {
    header: "Lot Number",
    accessorKey: "lot_number",
    cell: ({ row }) => row.original.lot_number ?? "Unassigned",
  },
  {
    header: "Order Qty",
    accessorKey: "order_quantity",
  },
  {
    header: "On Hand",
    accessorKey: "quantity_on_hand",
  },
  {
    header: "Avg. Unit Cost",
    accessorKey: "avg_unit_cost",
    cell: ({ row }) => formatUSD(row.original.avg_unit_cost),
  },
  {
    header: "Total Cost",
    accessorKey: "total_cost",
    cell: ({ row }) => formatUSD(row.original.total_cost),
  },
  {
    header: "First Received",
    accessorKey: "earliest_received_at",
    cell: ({ row }) =>
      row.original.earliest_received_at
        ? safeFormat(row.original.earliest_received_at)
        : "Pending",
  },
  {
    header: "Action",
    accessorKey: "action",
    meta: { skipLink: true },
    cell: ({ row }) => row.original.action,
  },
];

function InventoryDetails() {
  const { inventoryId } = Route.useParams();
  const [queryStates] = useQueryStates(dataTableSearchParsers);

  const inventory = useSuspenseQuery(
    suspenseOptions(getGetInventoryQueryOptions(inventoryId)),
  );

  const inventoryLots = useGetInventoryLots(
    inventoryId,
    {
      page: queryStates.page,
      limit: queryStates.limit,
      search: queryStates.search,
      sort_column: queryStates.sort_column,
      ascending: queryStates.ascending,
    },
    {
      query: {
        placeholderData: keepPreviousData,
      },
    },
  );

  const inv = inventory.data;
  const quantityOnHand = inv?.quantity_on_hand ?? 0;
  const reorderLevel = inv?.reorder_level ?? null;
  const isLowStock = reorderLevel !== null && quantityOnHand <= reorderLevel;

  const lotData: LotTableProps[] =
    inventoryLots.data?.items.map((lot) => ({
      link: lot.lot_number
        ? linkOptions({
            to: "/inventory/$inventoryId/lot/$lotNumber",
            params: {
              inventoryId,
              lotNumber: lot.lot_number,
            },
          })
        : linkOptions({
            to: "/inventory/$inventoryId/lot/unassigned",
            params: { inventoryId },
          }),
      lot_number: lot.lot_number ?? null,
      order_quantity: lot.order_quantity,
      quantity_on_hand: lot.quantity_on_hand,
      avg_unit_cost: lot.avg_unit_cost,
      total_cost: lot.total_cost,
      earliest_received_at: lot.earliest_received_at ?? null,
      earliest_expiration_date: lot.earliest_expiration_date ?? null,
      action: lot.lot_number ? (
        <EditLotActionDialog
          inventoryId={inventoryId}
          lotNumber={lot.lot_number}
          earliestExpirationDate={lot.earliest_expiration_date ?? undefined}
          onSuccess={() => inventoryLots.refetch()}
        />
      ) : (
        <AssignLotModal
          inventoryId={inventoryId}
          prefillExpirationDate={lot.earliest_expiration_date ?? undefined}
          onSuccess={() => inventoryLots.refetch()}
          trigger={
            <Button
              variant="link"
              size="sm"
              className="px-1"
              onClick={(e) => e.stopPropagation()}
            >
              Reassign Lot
            </Button>
          }
        />
      ),
    })) ?? [];

  return (
    <div>
      <title>
        {`${inv?.product.display_name ?? "Product"} Inventory - Legrande Health Portal`}
      </title>
      <Header
        crumbs={[
          { name: "Inventory", link: { to: "/inventory/lookup" } },
          {
            name: "Product Inventory",
            link: {
              to: "/inventory/$inventoryId",
              params: { inventoryId },
            },
          },
        ]}
      />
      <div className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {inv?.product.display_name}
              {isLowStock && <Badge variant="destructive">Low Stock</Badge>}
            </CardTitle>
            <CardDescription>{inv?.product.entity.name}</CardDescription>
            <CardAction>
              <div className="flex gap-2">
                <Button variant="outline" asChild>
                  <Link
                    to="/inventory/$inventoryId/edit"
                    params={{ inventoryId }}
                  >
                    <Pencil />
                    Edit
                  </Link>
                </Button>
                <MassAdjustmentModal
                  trigger={
                    <Button variant="outline">
                      <ClipboardList />
                      Mass Adjustment
                    </Button>
                  }
                  productId={inv?.product.product_id}
                  entityId={inv?.product.entity_id}
                />
                <AddInventoryLotModal
                  inventoryId={inventoryId}
                  refetch={() => {
                    inventory.refetch();
                    inventoryLots.refetch();
                  }}
                />
              </div>
            </CardAction>
          </CardHeader>
          <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <div className="text-sm text-muted-foreground">
                Quantity on Hand
              </div>
              <div className="text-2xl font-bold">{quantityOnHand}</div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground">Incoming</div>
              <div className="text-2xl font-bold">
                {inv?.incoming_quantity ?? 0}
              </div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground">Reorder Level</div>
              <div className="text-2xl font-bold">{reorderLevel ?? "--"}</div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground">Product Type</div>
              <div className="text-2xl font-bold">
                {inv?.product.catalog_product.product_base.product_type}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Inventory Lots</CardTitle>
            <CardDescription>
              Inventory lots for this product. Click a lot to see transaction
              history.
            </CardDescription>
            <CardAction>
              <IncomingOrdersDialog
                inventoryId={inventoryId}
                refetch={() => {
                  inventory.refetch();
                  inventoryLots.refetch();
                }}
              />
            </CardAction>
          </CardHeader>
          <CardContent>
            <ParamDataTableToolbar>
              <ParamSearchFilter isFetching={inventoryLots.isFetching} />
              <ParamClearButton />
            </ParamDataTableToolbar>
            <ParamDataTable
              columns={lotColumns}
              data={lotData}
              isLoading={inventoryLots.isLoading}
              isError={inventoryLots.isError}
              count={inventoryLots.data?.count}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
